import { useState, type ReactElement } from 'react';

import { absoluteUrl, displayLink } from '../routing/paths.js';

import { ColumnCorner } from './ColumnCorner.js';
import { PartyQrCode } from './PartyQrCode.js';
import { pillButton } from './pillButton.js';
import { RoomModal } from './RoomModal.js';
import { ShareActions } from './ShareActions.js';

const SHEET_QR_SIZE_PX = 220;

const SHARE_LABEL = 'Share this wrapped';

const ShareIcon = (): ReactElement => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="size-[1.2em]">
    <circle cx="15" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="5" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="15" cy="15.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M7.25 8.8 12.75 5.7M7.25 11.2l5.5 3.1"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

type WrappedShareDockProps = {
  readonly path: string;
  readonly shareText: string;
};

const ShareSheet = ({
  path,
  link,
  shareText,
  onClose,
}: {
  readonly path: string;
  readonly link: string;
  readonly shareText: string;
  readonly onClose: () => void;
}): ReactElement => (
  <RoomModal title={SHARE_LABEL} onClose={onClose}>
    <PartyQrCode
      path={path}
      size={SHEET_QR_SIZE_PX}
      alwaysVisible
      caption="Scan to open"
      alt={`QR code linking to ${link}`}
      className="p-4"
    />
    <p className="font-display text-content w-full text-3xl break-all sm:text-5xl">{link}</p>
    <ShareActions url={absoluteUrl(path)} shareText={shareText} />
  </RoomModal>
);

/**
 * One surface, two shapes: a room-facing code parked in the corner of a big
 * screen, and on a phone a small floating button within thumb reach that opens
 * the same kit full-screen.
 */
export const WrappedShareDock = ({ path, shareText }: WrappedShareDockProps): ReactElement => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const link = displayLink(path);

  return (
    <>
      <div className="hidden md:block">
        <ColumnCorner edge="bottom">
          <div className="flex flex-col items-center gap-3">
            <PartyQrCode
              path={path}
              alwaysVisible
              caption="Scan to open"
              className="shadow-raised"
            />
            <ShareActions url={absoluteUrl(path)} shareText={shareText} />
          </div>
        </ColumnCorner>
      </div>

      {/* Stacked directly above the globally fixed settings cog rather than beside it: the
          recap's own Back/Pause/Next pills run across the bottom centre, so the corner stack
          has to stay one control wide. */}
      <button
        type="button"
        aria-label={SHARE_LABEL}
        onClick={() => {
          setIsSheetOpen(true);
        }}
        className={`${pillButton('quiet-strong', 'md')} bg-surface shadow-card fixed bottom-[5.5rem] left-4 z-30 flex items-center gap-1.5 md:hidden`}
      >
        <ShareIcon />
        Share
      </button>

      {isSheetOpen ? (
        <ShareSheet
          path={path}
          link={link}
          shareText={shareText}
          onClose={() => {
            setIsSheetOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
